import { useMutation } from '@tanstack/react-query'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import type { ToastItem } from '@mobi/component-library/Feedback/Toast/types'
import { fetchCampaignsAsync } from '@core/State/UserAccount/async-signals'
import { globalToastEmitter } from '@mobi/component-library/Feedback/Toast/helpers/events'
import { patchSplitBonusBet } from '../data/api'
import { trackConfirmBonusSplit } from '../analytics'

type UseSplitBonusBetMutationProps = {
  updateBonusBetToSplit: () => void
  toastEmitter?: typeof globalToastEmitter
}

export const useSplitBonusBetMutation = ({
  updateBonusBetToSplit,
  toastEmitter,
}: UseSplitBonusBetMutationProps) => {
  return useMutation({
    mutationFn: async ({
      bonusBetId,
      numberOfTokens,
    }: {
      bonusBetId: number
      numberOfTokens: number
    }) => await patchSplitBonusBet(bonusBetId, numberOfTokens),

    onSuccess: async (_data, variables) => {
      await fetchCampaignsAsync()
      updateBonusBetToSplit()
      trackConfirmBonusSplit(`${variables.numberOfTokens}x`)
      const successToast = createToastProps(
        'bet-split-success',
        'Bonus Bet has been split successfully'
      )
      if (toastEmitter) {
        toastEmitter.emit('addToast', successToast)
        return () => toastEmitter.emit('removeToast', { id: 'bet-split-success' })
      } else {
        addToast(successToast)
        return
      }
    },

    onError: () => {
      updateBonusBetToSplit()
      const failureToast = createToastProps('bet-split-failure', 'Failed to split Bonus Bet')
      if (toastEmitter) {
        toastEmitter.emit('addToast', failureToast)
        return () => toastEmitter.emit('removeToast', { id: 'bet-split-failure' })
      } else {
        addToast(failureToast)
        return
      }
    },
  })
}

// =============
// Local Helpers
// =============

const createToastProps = (id: string, title: string): ToastItem => {
  return {
    id,
    title,
    timeoutMs: 5000,
    isDismissable: true,
  }
}
